import { observer } from "mobx-react-lite";
import React, { useRef, useState, useEffect } from "react";
import { StoreState } from "../../../enum/StoreState";
import { useStores } from "../../../stores/setup/use-store";
import OperationRuleLocationDrawer from "./Components/OperationRuleLocationDrawer";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "./styles.css";
import "video-react/dist/video-react.css";
import { IOperationRule } from "../../../stores/data/operationRules/OperationRuleModel";
import {
  Box,
  Select,
  Button,
  Flex,
  FormLabel,
  useDisclosure,
  AlertIcon,
  Alert,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import AddIcon from "@material-ui/icons/Add";
import EmptyState from "../../../components/EmptyState/EmptyState";
import { IOperationRuleLocation } from "../../../stores/data/operationRuleLocations/OperationRuleLocationModel";
import { AlertDelete } from "../../../components/AlertDelete/AlertDelete";
import Loading from "../../../components/Loading/Loading";
import i18n from "../../../i18n";
import DraggableTable from "components/DraggableTable";
import {
  useLocationForms,
  useMapStations,
  useLocationDataInitialization,
  useDashboardSelection,
  useLocationOperations,
  useLocationOrdering,
} from "./hooks";
import { createCustomMarkerIcon } from "./util/createCustomMarkerIcon";
import { LocationFormValues } from "./types";
import { useLocation, useHistory } from "react-router-dom";

const OperationRuleLocationDetail: React.FC = () => {
  const {
    dataStores: {
      operationRuleStore,
      operationRuleLocationStore,
      authStore,
      oceanWeatherIntelligenceStore,
      insightAlertStore,
      regionStore,
    },
  } = useStores();

  const history = useHistory();

  const [locationRemove, setLocationRemove] =
    useState<IOperationRuleLocation | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const hasProcessedUrlParams = useRef(false);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const initialRef = useRef(null);

  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();

  const { t } = useTranslation("translation", {
    keyPrefix: "oceanWeatherIntelligence.operationRuleLocation",
  });

  const location = useLocation();

  const { formikCreate, formikEdit } = useLocationForms();
  const { allStations, centerMap } = useMapStations();
  useLocationDataInitialization();
  const { handleSelectDashboardChange } = useDashboardSelection();
  const { handleOnEdit, handleOnConfirmDelete, handleAddOrEditData } =
    useLocationOperations();
  const { handleSaveOrder } = useLocationOrdering();

  const clearUrlParams = () => {
    history.replace(window.location.pathname);
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const createLocation = queryParams.get("create_location");
    const editLocationId = queryParams.get("edit_location_id");

    if (!createLocation && !editLocationId) {
      hasProcessedUrlParams.current = false;
      return;
    }

    if (hasProcessedUrlParams.current) {
      return;
    }

    if (createLocation === "true") {
      hasProcessedUrlParams.current = true;
      setIsEditing(false);

      if (operationRuleLocationStore.createFormValues) {
        const savedValues = operationRuleLocationStore.createFormValues;
        Object.keys(savedValues).forEach((key) => {
          formikCreate.setFieldValue(
            key as keyof LocationFormValues,
            (savedValues as any)[key]
          );
        });
      }

      if (oceanWeatherIntelligenceStore.selectedGeofence?.length > 0) {
        formikCreate.setFieldValue(
          "geofence",
          oceanWeatherIntelligenceStore.selectedGeofence
        );

        if (insightAlertStore.macroRegionOrigin) {
          const macroRegionData = regionStore.macroRegions.find(
            (mr) => mr.origin === insightAlertStore.macroRegionOrigin
          );
          if (macroRegionData) {
            formikCreate.setFieldValue("macroRegionId", macroRegionData.id);
          }
        }
      }

      setTimeout(() => {
        onOpen();
      }, 100);

      clearUrlParams();

      setTimeout(() => {
        oceanWeatherIntelligenceStore.setSelectedGeofence([]);
        insightAlertStore.setMacroRegionOrigin(null);
      }, 200);
    } else if (editLocationId) {
      hasProcessedUrlParams.current = true;
      const locationIdNumber = parseInt(editLocationId, 10);
      const locationToEdit =
        operationRuleLocationStore.operationRuleLocations.find(
          (loc) => loc.id === locationIdNumber
        );

      if (locationToEdit) {
        const hasEditedGeofence =
          oceanWeatherIntelligenceStore.selectedGeofence?.length > 0;

        if (hasEditedGeofence) {
          const updatedLocation: IOperationRuleLocation = {
            ...locationToEdit,
            geofence: oceanWeatherIntelligenceStore.selectedGeofence,
          };
          handleOnEdit(updatedLocation, formikEdit, onOpen, setIsEditing);

          if (insightAlertStore.macroRegionOrigin) {
            const macroRegion = regionStore.macroRegions.find(
              (mr) => mr.origin === insightAlertStore.macroRegionOrigin
            );
            if (macroRegion) {
              formikEdit.setFieldValue("macroRegionId", macroRegion.id);
            }
          }
        } else {
          handleOnEdit(locationToEdit, formikEdit, onOpen, setIsEditing);
        }
      }

      clearUrlParams();

      setTimeout(() => {
        oceanWeatherIntelligenceStore.setSelectedGeofence([]);
        insightAlertStore.setMacroRegionOrigin(null);
      }, 200);
    }
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOpenDeleteModal = (location: IOperationRuleLocation) => {
    setLocationRemove(location);
    onDeleteOpen();
  };

  const handleOpenAddModal = () => {
    setIsEditing(false);
    onOpen();
    formikCreate.resetForm();
    operationRuleLocationStore.setOperationRuleLocation(undefined);
    oceanWeatherIntelligenceStore.setSelectedGeofence([]);
    insightAlertStore.setMacroRegionOrigin(null);
  };

  const handleCloseDrawer = () => {
    onClose();
    operationRuleLocationStore.setOperationRuleLocation(undefined);
    insightAlertStore.setIsGeofenceInvalid(false);
    oceanWeatherIntelligenceStore.setSelectedGeofence([]);
    insightAlertStore.setMacroRegionOrigin(null);
  };

  const handleEdit = (value: IOperationRuleLocation) => {
    handleOnEdit(value, formikEdit, onOpen, setIsEditing);
  };

  const handleConfirmDelete = () => {
    handleOnConfirmDelete(locationRemove, onDeleteClose, setLocationRemove);
  };

  const handleSubmit = (
    values: LocationFormValues,
    isEditingMode?: boolean
  ) => {
    handleAddOrEditData(values, isEditingMode || false, onClose);
  };

  return (
    <Box>
      {operationRuleLocationStore.state === StoreState.ERROR && (
        <Alert status="error" mb={4} color="black">
          <AlertIcon />
          {operationRuleLocationStore.messageError}
        </Alert>
      )}
      {authStore.readOnlyUser && (
        <Alert bg="#bee3f8" status="info" mb={4} color="gray.700">
          <AlertIcon />
          {i18n.t("readingModePleaseContact")}
        </Alert>
      )}

      {operationRuleLocationStore.state === StoreState.PEDDING && <Loading />}

      <OperationRuleLocationDrawer
        formik={isEditing ? formikEdit : formikCreate}
        isOpen={isOpen}
        onClose={handleCloseDrawer}
        initialRef={initialRef}
        onConfirm={handleSubmit}
        title={isEditing ? t("editLocation") : t("addNewLocation")}
        confirmButtonText={
          isEditing ? (t("save") as string) : (t("add") as string)
        }
        isEditing={isEditing}
      />

      <AlertDelete
        isOpen={isDeleteOpen}
        onClose={onDeleteClose}
        titleLabel={t("deleteLocation")}
        bodyText={t("areYouSureCantUndoThis", {
          locationName: locationRemove?.name,
        })}
        cancelButtonLabel={t("cancel")}
        deleteButtonLabel={t("delete")}
        handleDelete={handleConfirmDelete}
      />

      <Flex>
        <Box>
          <FormLabel color={"newBlue.500"}>{t("selectDashboard")}</FormLabel>
          <Select
            w={"260px"}
            borderRadius={8}
            color={"newBlue.500"}
            backgroundColor="#ffffff"
            size="md"
            key={`selectDashboard-${operationRuleStore.operationRuleMainId}`}
            onChange={(e) =>
              handleSelectDashboardChange(parseInt(e.target.value))
            }
            value={operationRuleStore.operationRuleMainId}
            _hover={{ borderColor: "newBlue.500" }}
            _focus={{ borderColor: "none" }}
            name={t("selectDashboard") as string}
          >
            {operationRuleStore.operationRules.map((value: IOperationRule) => (
              <option
                style={{ color: "black" }}
                value={value.id}
                key={value.id}
              >
                {value.name}
              </option>
            ))}
          </Select>
        </Box>
        {!authStore.readOnlyUser && (
          <Button
            mt={8}
            ml={4}
            layerStyle="ocean"
            _hover={{ layerStyle: "ocean" }}
            leftIcon={<AddIcon />}
            onClick={handleOpenAddModal}
          >
            {t("addNewLocation")}
          </Button>
        )}
      </Flex>

      <Box mt={4}>
        <MapContainer
          key={`mapLocationStation-${centerMap}`}
          center={centerMap}
          zoom={5}
          zoomControl={true}
          scrollWheelZoom={true}
          attributionControl={false}
          style={{ height: "25vh", borderRadius: "16px" }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.i4sea.com">i4sea</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {allStations.map((value) =>
            value.station_lat !== null && value.station_lon ? (
              <Marker
                key={`marker${value.station_id}-${value.macro_region_origin}`}
                position={[value.station_lat, value.station_lon]}
                icon={createCustomMarkerIcon()}
              >
                <Popup>{value.station_name}</Popup>
              </Marker>
            ) : (
              ""
            )
          )}
        </MapContainer>
      </Box>

      {operationRuleLocationStore.operationRuleLocations?.length > 0 && (
        <DraggableTable
          data={operationRuleLocationStore.operationRuleLocations}
          columns={[{ key: "name", header: t("name"), width: "100%" }]}
          onEdit={handleEdit}
          onDelete={handleOpenDeleteModal}
          showDeleteButton={!authStore.readOnlyUser}
          disableDragAndDrop={!authStore.adminUser}
          tableStyle={{
            color: "#71717A",
            backgroundColor: "#FAFAFA",
            marginTop: "10px",
          }}
          rowStyle={{
            backgroundColor: "white",
            borderColor: "rgba(30, 82, 151, 0.12)",
            color: "newBlue.500",
            height: "56px",
          }}
          dragStyle={{
            backgroundColor: "#D8E7F0",
            boxShadow: "0 4px 12px rgba(119, 176, 213, 0.3)",
          }}
          onSaveOrder={handleSaveOrder}
        />
      )}

      {!operationRuleLocationStore.operationRuleLocations?.length &&
        !operationRuleLocationStore.hasLocations && (
          <EmptyState
            key={`id-${operationRuleLocationStore.hasLocations}`}
            description={t("addNewLocationToYourDashboard")}
            buttonAction={handleOpenAddModal}
            buttonIcon={<AddIcon />}
            buttonText={
              !authStore.readOnlyUser
                ? (t("addNewLocation") as string)
                : undefined
            }
          />
        )}
    </Box>
  );
};

export default observer(OperationRuleLocationDetail);
